import { apiError } from "../../../../../../../../utils/formErrors";
import React, { useState } from "react";
import { Box, Stack, CircularProgress } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useOutletContext } from "react-router";
import BtnPrimary from "../../../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../../../component/SecondaryButton";
import FormField from "../../../../../../../../component/Formfield";
import TypeBtn from "../../../../../../../../component/AddRemoveButton";
import EmptyState from "../../../../../../../../component/EmptyState";
import { updateEmployeeDetailsById } from "../../../../../../../../services/employee";
import { useNotify } from "../../../../../../../../component/NotificationProvider";
import { tokens, radius } from "../../../../../../../../theme";

import { pressable } from "../../../../../../../../component/pressableProps";
const dateInputSx = {
  width: "100%", boxSizing: "border-box", fontFamily: "inherit", fontSize: "0.875rem", color: tokens.text,
  border: `1px solid ${tokens.borderStrong}`, borderRadius: "8px", padding: "11px 14px",
  background: tokens.card, outline: "none", "&:hover": { borderColor: tokens.textMuted }, "&:focus": { borderColor: tokens.primary, boxShadow: `inset 0 0 0 1px ${tokens.primary}` },
};
const Label = ({ children }) => <Box sx={{ fontSize: "0.8125rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>{children}</Box>;
const toInput = (d) => (d ? new Date(d).toISOString().split("T")[0] : "");
const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB") : "—");

// read-only row
const HistoryRow = ({ title, subtitle, from, to }) => (
  <Box sx={{ p: "1rem 1.25rem", border: `1px solid ${tokens.border}`, borderRadius: "12px" }}>
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={1}>
      <Box>
        <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{title || "—"}</Box>
        <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{subtitle || "—"}</Box>
      </Box>
      <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted }}>{fmt(from)} – {fmt(to)}</Box>
    </Stack>
  </Box>
);

const ProfileWorkHistory = () => {
  const [employeeData] = useOutletContext();
  const notify = useNotify();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [education, setEducation] = useState(() => (employeeData?.education || []).map((e) => ({ ...e, startDate: toInput(e.startDate), endDate: toInput(e.endDate) })));
  const [experience, setExperience] = useState(() => (employeeData?.experience || []).map((e) => ({ ...e, startDate: toInput(e.startDate), endDate: toInput(e.endDate) })));

  const cancel = () => {
    setEducation((employeeData?.education || []).map((e) => ({ ...e, startDate: toInput(e.startDate), endDate: toInput(e.endDate) })));
    setExperience((employeeData?.experience || []).map((e) => ({ ...e, startDate: toInput(e.startDate), endDate: toInput(e.endDate) })));
    setEditing(false);
  };

  const save = async () => {
    // keep only complete rows (schema requires all fields)
    const edu = education.filter((e) => e.level && e.institution && e.startDate && e.endDate);
    const exp = experience.filter((e) => e.designation && e.organization && e.startDate && e.endDate);
    setSaving(true);
    try {
      const res = await updateEmployeeDetailsById(employeeData?._id, { education: edu, experience: exp });
      if (res?.success === false) throw apiError(res, "Could not save");
      notify.success("Work history updated");
      setEditing(false);
    } catch (e) {
      notify.error(e?.message || "Could not save work history");
    } finally {
      setSaving(false);
    }
  };

  const setExp = (i, f, v) => setExperience((p) => p.map((row, idx) => (idx === i ? { ...row, [f]: v } : row)));
  const setEdu = (i, f, v) => setEducation((p) => p.map((row, idx) => (idx === i ? { ...row, [f]: v } : row)));

  const Section = ({ title, children, action }) => (
    <Box sx={{ background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: `${radius.card}px`, p: "1.5rem", mb: "1.5rem" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.25rem" }}>
        <Box sx={{ fontSize: "0.9375rem", fontWeight: 600, color: tokens.text }}>{title}</Box>
        {action}
      </Stack>
      {children}
    </Box>
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: "1rem" }}>
        {editing ? (
          <Stack direction="row" spacing={1}>
            <BtnSecondary onClick={cancel} disabled={saving} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
            <BtnPrimary onClick={save} disabled={saving} sx={{ borderRadius: "8px" }}>
              {saving ? <CircularProgress size={"1rem"} /> : "Save Changes"}
            </BtnPrimary>
          </Stack>
        ) : (
          <BtnSecondary onClick={() => setEditing(true)} sx={{ borderRadius: "8px" }}>Edit</BtnSecondary>
        )}
      </Stack>

      <Section title="Work Experience">
        {!editing ? (
          experience.length === 0 ? <EmptyState title="No experience recorded" /> : (
            <Stack spacing={1.25}>
              {experience.map((e, i) => <HistoryRow key={i} title={e.designation} subtitle={e.organization} from={e.startDate} to={e.endDate} />)}
            </Stack>
          )
        ) : (
          <Stack spacing={1.5}>
            {experience.map((e, i) => (
              <Stack key={i} direction={{ xs: "column", md: "row" }} alignItems={{ md: "flex-end" }} spacing={1.5}
                sx={{ p: "1rem 1.25rem", borderRadius: "12px", border: `1px solid ${tokens.border}`, background: tokens.surface }}>
                <Box sx={{ flex: 1 }}><Label>Designation</Label><FormField type="text" value={e.designation} onChange={(ev) => setExp(i, "designation", ev.target.value)} fullwidth /></Box>
                <Box sx={{ flex: 1 }}><Label>Organization</Label><FormField type="text" value={e.organization} onChange={(ev) => setExp(i, "organization", ev.target.value)} fullwidth /></Box>
                <Box sx={{ flex: 1 }}><Label>From</Label><Box component="input" type="date" value={e.startDate} onChange={(ev) => setExp(i, "startDate", ev.target.value)} sx={dateInputSx} /></Box>
                <Box sx={{ flex: 1 }}><Label>To</Label><Box component="input" type="date" value={e.endDate} onChange={(ev) => setExp(i, "endDate", ev.target.value)} sx={dateInputSx} /></Box>
                <Box {...pressable(() => setExperience((p) => p.filter((_, idx) => idx !== i)), "Remove")} sx={{ cursor: "pointer", color: tokens.danger, pb: "0.75rem" }}><DeleteOutlineIcon /></Box>
              </Stack>
            ))}
            <Box><TypeBtn type="add" onClick={() => setExperience([...experience, { designation: "", organization: "", startDate: "", endDate: "" }])}>Add Experience</TypeBtn></Box>
          </Stack>
        )}
      </Section>

      <Section title="Education">
        {!editing ? (
          education.length === 0 ? <EmptyState title="No education recorded" /> : (
            <Stack spacing={1.25}>
              {education.map((e, i) => <HistoryRow key={i} title={e.level} subtitle={e.institution} from={e.startDate} to={e.endDate} />)}
            </Stack>
          )
        ) : (
          <Stack spacing={1.5}>
            {education.map((e, i) => (
              <Stack key={i} direction={{ xs: "column", md: "row" }} alignItems={{ md: "flex-end" }} spacing={1.5}
                sx={{ p: "1rem 1.25rem", borderRadius: "12px", border: `1px solid ${tokens.border}`, background: tokens.surface }}>
                <Box sx={{ flex: 1 }}><Label>Level / Degree</Label><FormField type="text" value={e.level} onChange={(ev) => setEdu(i, "level", ev.target.value)} fullwidth /></Box>
                <Box sx={{ flex: 1 }}><Label>Institution</Label><FormField type="text" value={e.institution} onChange={(ev) => setEdu(i, "institution", ev.target.value)} fullwidth /></Box>
                <Box sx={{ flex: 1 }}><Label>From</Label><Box component="input" type="date" value={e.startDate} onChange={(ev) => setEdu(i, "startDate", ev.target.value)} sx={dateInputSx} /></Box>
                <Box sx={{ flex: 1 }}><Label>To</Label><Box component="input" type="date" value={e.endDate} onChange={(ev) => setEdu(i, "endDate", ev.target.value)} sx={dateInputSx} /></Box>
                <Box {...pressable(() => setEducation((p) => p.filter((_, idx) => idx !== i)), "Remove")} sx={{ cursor: "pointer", color: tokens.danger, pb: "0.75rem" }}><DeleteOutlineIcon /></Box>
              </Stack>
            ))}
            <Box><TypeBtn type="add" onClick={() => setEducation([...education, { level: "", institution: "", startDate: "", endDate: "" }])}>Add Education</TypeBtn></Box>
          </Stack>
        )}
      </Section>
    </Box>
  );
};

export default ProfileWorkHistory;
